// Whole Self Resources Content - Structured for clean display

// Custom logo icon for section markers
export const SECTION_ICON = 'https://customer-assets.emergentagent.com/job_hello-world-4084/artifacts/vnl8646a_Grounded%20Wellness%20%28Logo%29%20%284%29.png';

export const wholeSelfContent = {
  'life-journey-out-of-crisis': {
    id: 'life-journey-out-of-crisis',
    title: 'A Life Journey: Out of Crisis',
    subtitle: 'From Survival to Becoming — A Path in Three Levels',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/44mzytzg_ChatGPT%20Image%20Sep%201%2C%202026%2C%2010_13_17%20AM.png',
    overviewImage: 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/vyyuxngl_ChatGPT%20Image%20Sep%201%2C%202026%2C%2010_21_26%20AM.png',
    overviewImageCaption: 'The Life Journey at a glance — the six stones across three levels: Foundation, Preparation and Transformation.',
    introduction: "You can't right the ship in a storm. This is a map for the journey out of crisis — not a rush to fix, but a gentle, ordered path from survival toward becoming. Move through it at the pace your system can hold. Each level rests on the one before it.",
    sections: [
      {
        id: 'foundation',
        type: 'prose',
        title: 'Foundation Level',
        paragraphs: ['First we create safety. Before insight, before change — the body and the environment need solid ground beneath them.'],
        groups: [
          {
            title: '1. Leave the Sea',
            tone: 'caution',
            items: [
              'Get out of crisis, toxicity, addiction, toxic relationships and unsafe environments.',
              'When grief is intense, seek counselling and/or give yourself time.',
              'Stabilize your mindset and your environment.',
              'You can\u2019t right the ship in a storm. Leave the sea. Find the shore.',
            ],
          },
          {
            title: '2. Arrive on the Shore',
            tone: 'default',
            items: [
              'Create healthy habits for the body.',
              'Prioritize the body\u2019s basic needs: sleep, nourishment, movement and exercise.',
              'Clean up your diet. Omit substances that keep you stuck.',
              'Begin to create safety and stability in the body.',
            ],
          },
          {
            title: '3. Retrain the Nervous System',
            tone: 'neutral',
            items: [
              'Learn to relax the body and mind. Practice it repetitively, daily, over and over.',
              'Create the \u201cchill factor.\u201d',
              'Teach your nervous system that it is safe to settle, soften and come out of survival mode.',
            ],
          },
        ],
      },
      {
        id: 'preparation',
        type: 'prose',
        title: 'Preparation Level',
        paragraphs: ['With safety established, we build the inner posture that makes deeper work possible — curiosity and neutrality.'],
        groups: [
          {
            title: '4. Observe',
            tone: 'default',
            items: [
              'Learn to observe your discomfort like a scientist or archaeologist.',
              'Become curious about what is happening without immediately trying to fix it.',
              'Practice no judgement.',
            ],
          },
          {
            title: '5. Neutrality',
            tone: 'neutral',
            items: [
              'Practice neutrality. Explore the Hermetic Principles (below).',
              'Learn to draw the poison out of your suffering by noticing where you are holding shame, blame, resistance or judgment \u2014 inwardly or outwardly.',
              'Feel what you feel without making yourself or someone else wrong.',
            ],
          },
        ],
      },
      {
        id: 'transformation',
        type: 'prose',
        title: 'Transformation Level',
        paragraphs: ['Now, from a settled and neutral place, energy that was held can finally move.'],
        groups: [
          {
            title: '6. Learn How to Release',
            tone: 'default',
            items: [
              'Learn how to cathartically release from the body.',
              'Use practices such as somatic work, compassion, fascia and other body-based practices to liberate stuck energy.',
              'Move from holding, suppressing and carrying \u2014 toward release, freedom and expansion.',
            ],
          },
        ],
      },
      {
        id: 'the-journey',
        type: 'prose',
        title: 'The Journey',
        callout: 'Leave the sea.\nFind the shore.\nStabilize.\nRetrain.\nObserve.\nFind neutrality.\nRelease.\nBecome who you are meant to be.',
      },
      {
        id: 'hermetic-principles',
        type: 'prose',
        title: 'The Seven Hermetic Principles',
        paragraphs: [
          'A companion for the practice of neutrality. The Hermetic Principles are seven timeless laws first set down in The Kybalion (1908) and rooted in the Hermetic tradition attributed to Hermes Trismegistus. They are offered here simply as contemplation \u2014 lenses to soften our grip and widen perspective.',
        ],
        groups: [
          { title: '1. Mentalism \u2014 \u201cThe All is Mind; the Universe is Mental.\u201d', tone: 'tcm', items: ['Reality arises from a universal, infinite mind. Everything we experience is, at some level, mental in nature.', 'Practice: observe and gently shift your thought patterns.'] },
          { title: '2. Correspondence \u2014 \u201cAs above, so below; as below, so above.\u201d', tone: 'tcm', items: ['Patterns repeat across every level of life. The small mirrors the large, and the large the small.', 'Practice: study your small behaviours as reflections of larger ones.'] },
          { title: '3. Vibration \u2014 \u201cNothing rests; everything moves.\u201d', tone: 'tcm', items: ['All matter and energy are in constant motion, differing only in frequency.', 'Practice: change your emotional state through movement or breath.'] },
          { title: '4. Polarity \u2014 opposites are two ends of one thing.', tone: 'tcm', items: ['Love and hate, hot and cold, light and dark are degrees along a single continuum.', 'Practice: reframe an extreme as simply a degree that can be shifted.'] },
          { title: '5. Rhythm \u2014 everything flows in cycles.', tone: 'tcm', items: ['Rising and falling, expanding and contracting govern nature and mind alike.', 'Practice: anticipate the cycles instead of fearing them.'] },
          { title: '6. Cause & Effect \u2014 nothing happens by chance.', tone: 'tcm', items: ['Every action has a cause; every cause produces an effect.', 'Practice: participate consciously and act deliberately.'] },
          { title: '7. Gender \u2014 the active and receptive live in all things.', tone: 'tcm', items: ['Masculine (active) and feminine (receptive) are metaphysical forces present in thought, creativity and manifestation \u2014 not strictly biological.', 'Practice: balance your active and receptive modes.'] },
        ],
        listTitle: 'A note on sources',
        listItems: [
          'The Emerald Tablet \u2014 origin of \u201cas above, so below.\u201d',
          'Corpus Hermeticum \u2014 philosophical dialogues on mind, creation and spiritual ascent.',
          'The Kybalion (1908) \u2014 a modern Hermetic tract by \u201cThree Initiates,\u201d likely William Walker Atkinson.',
        ],
        callout: 'These principles are shared for reflection and education only \u2014 a starting point for our own contemplation, not doctrine.',
      },
    ],
    quotes: [
      'You can\u2019t right the ship in a storm. Leave the sea. Find the shore.',
      'Become who you are meant to be.',
    ],
  },
  'all-healing-is-self-healing': {
    id: 'all-healing-is-self-healing',
    title: 'How Do We Move from Constriction to Expansion?',
    subtitle: 'Understanding Our Inner Landscape',
    image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800',
    introduction: "At its most basic level, human suffering has a texture. What I do as a coach is not that special. I'm not really supposed to say that. What I value most is when someone finds a way to untie themselves in my presence as I act like a mirror — helping them see where they've locked themselves up in constrictive thoughts and beliefs.",
    constrictionExpansion: {
      title: "Constriction vs Expansion",
      icon: "logo",
      constriction: {
        title: "Constriction",
        description: "Feels tight. It narrows the lens. The breath shortens. The body braces. Thoughts loop. Life feels heavy, effortful, and sharp around the edges. We often don't like ourselves here."
      },
      expansion: {
        title: "Expansion",
        description: "Feels different. There's space. Breath moves. Curiosity returns. The body softens without collapsing. Life feels lighter, wider, more possible. We often like ourselves here :)"
      },
      insight: "The real question isn't what constriction and expansion are, but what pulls us into one and allows us to return to the other."
    },
    whatCreatesConstriction: {
      title: "What Creates Constriction?",
      icon: "logo",
      intro: "Constriction rarely announces itself dramatically. It's subtle. Quiet. Reasonable.",
      signs: [
        "Self-criticism disguised as motivation",
        "Holding tension as if it's keeping us together",
        "Emotional bracing",
        "A belief that something needs fixing",
        "Living in reaction rather than response"
      ],
      root: "At the root of constriction is usually self-opposition.",
      stances: [
        "I'm not enough yet",
        "I need to be different to be okay",
        "Something is wrong with me",
        "I can't trust myself"
      ],
      impact: "When we don't like ourselves, even slightly, life tightens. The nervous system senses threat. The body contracts. The mind narrows to protection and control. Pain doesn't just come from injury or circumstance. It often comes from how tightly we're holding ourselves while living our lives."
    },
    expansionSection: {
      title: "Expansion Isn't Something We Force",
      icon: "logo",
      intro: "Expansion isn't about becoming better, higher, or more evolved. It's about removing the inner pressure.",
      showsUp: [
        "We soften our grip on self-judgment",
        "We stop fighting our humanity",
        "We feel safe to feel what's happening",
        "We allow curiosity instead of condemnation"
      ],
      key: "Expansion doesn't require confidence. It requires friendliness."
    },
    simplestTruth: {
      title: "The Simplest Truth",
      icon: "logo",
      content: "If we like ourselves, life feels good. If we don't like ourselves, life does NOT feel good. Most people would never speak to a friend the way they speak to themselves. Or we would never speak to another, the way we speak to ourselves."
    },
    bestFriend: {
      title: "Are We Our Own Best Friend?",
      icon: "logo",
      intro: "Being your own best friend doesn't mean constant positivity. It means non-abandonment.",
      means: [
        "Staying present with yourself",
        "Not making yourself wrong for being human",
        "Listening to yourself instead of correcting",
        "Offering support before solutions"
      ],
      key: "Expansion happens when the inner environment becomes safe. Not perfect. Safe."
    },
    mindfulness: {
      title: "Why Mindfulness Matters",
      icon: "logo",
      intro: "Mindfulness isn't about being calm all the time. It's about noticing — when we've tightened, when breath is shallow, when the inner voice is harsh.",
      insight: "Without awareness, constriction runs the show unconsciously. With awareness, we get choice.",
      questions: [
        "What am I believing right now?",
        "Am I being kind to myself in this moment?",
        "Is this response creating more contraction... or more space?"
      ],
      truth: "This is why the work is always an inside job. No posture, supplement, relationship, or achievement can override a hostile inner environment."
    },
    theShift: {
      title: "From Constriction to Expansion: The Shift",
      icon: "logo",
      intro: "The shift doesn't usually happen through effort. It happens through permission. Permission to soften. Permission to feel. Permission to be imperfect without punishment.",
      note: "That's WHY it's so hard! We've been rewarded most of our lives for hard work.",
      moves: [
        { from: "Self-surveillance", to: "Self-trust" },
        { from: "Control", to: "Curiosity" },
        { from: "Judgment", to: "Understanding" }
      ],
      insight: "It's less about 'doing the right thing' and more about relating differently to ourselves while doing anything at all."
    },
    finalThought: {
      title: "A Final Thought",
      icon: "logo",
      content: "Expansion isn't a destination. It's a relationship. A relationship with your breath. Your body. Your emotions. Your thoughts. Your humanity. And like all good relationships, it thrives on respect, patience, and a little humor. Because when you genuinely like yourself, even on the days you're messy, tired, or unsure... life doesn't have to fight you anymore."
    },
    quotes: [
      "Pain doesn't just come from injury or circumstance. It often comes from how tightly we're holding ourselves while living our lives.",
      "Thank you BODY for carrying what I could not cognitively hold.",
      "When you genuinely like yourself, even on the days you're messy, tired, or unsure... life doesn't have to fight you anymore."
    ],
    books: [
      { title: "The Art of Power", author: "Thich Nhat Hanh" }
    ]
  },
  'mind-house': {
    id: 'mind-house',
    title: 'Mind House',
    subtitle: 'Meditation 101',
    image: 'https://images.unsplash.com/photo-1508672019048-805c876b67e2?w=800',
    introduction: "Meditation can be used for many different reasons and many different methods are available. Meditation has been shown to reduce stress and create a sense of well-being. Meditation is not a cure for stress, rather it can help balance the mind and body to create a calming sense which can lead to clearer thinking, wise view, and settling heightened emotional states.",
    sources: {
      title: "This resource draws from:",
      items: [
        "Stephen Wolinski's book – 'Waking from the Trance: A Practical Course for Developing Multi-Dimensional Awareness'",
        "MBSR (Mind Body Stress Reduction Method) developed by Jon Kabat-Zinn"
      ]
    },
    levelsOfAwareness: {
      title: "6 Levels of Awareness",
      subtitle: "From Quantum Psychology Philosophy",
      icon: "logo",
      levels: [
        { number: 6, name: "External" },
        { number: 5, name: "Biological" },
        { number: 4, name: "Mental" },
        { number: 3, name: "Emotional" },
        { number: 2, name: "Essence" },
        { number: 1, name: "I AM" }
      ],
      practice: {
        title: "Working at the I AM Level",
        content: "Define or consider any word or concept through NOT going into thoughts, memories, emotions, perceptions, assumptions, associations or intentions. First label, then discard. We are removing the story. Continue to brainstorm, labeling and discarding — 'not this, not that.'"
      }
    },
    attitudinalFactors: {
      title: "7 Attitudinal Factors",
      icon: "logo",
      note: "Notice that 'Letting Go' is the 7th step in a 7-step process!",
      factors: [
        "Non-judging",
        "Patience",
        "A beginner's mind",
        "Trust",
        "Non-striving",
        "Acceptance",
        "Letting Go"
      ]
    },
    preconditions: {
      title: "Preconditions for Healing",
      icon: "logo",
      items: ["Stop", "Calming", "Resting"],
      insight: "When we humans get sick we don't rest, we worry. We run off and find doctors or treatments... 'we shouldn't be sick.' But the body needs us to stop."
    },
    fiveStages: {
      title: "5 Stages",
      subtitle: "The Buddha taught many techniques to help us calm our body and mind and look deeply at them",
      icon: "logo",
      stages: [
        { number: 1, name: "Recognition" },
        { number: 2, name: "Acceptance" },
        { number: 3, name: "Embracing" },
        { number: 4, name: "Looking Deeply" },
        { number: 5, name: "Insight" }
      ]
    },
    sevenHomecomings: {
      title: "7 Homecomings",
      subtitle: "A reminder for gathering the necessary tools and resources",
      icon: "logo",
      intro: "The best way to begin is by reading through the guiding instructions once, and informally evoking each homecoming. Let the language and process change to meet your needs. The guiding instructions are just a template. Allowing yourself to be creative is important to sustain the practice.",
      items: [
        "Guide/teacher/mentor",
        "Wisdom texts",
        "Communities",
        "Ancestors",
        "Earth",
        "Yourself",
        "All"
      ],
      closing: "Imagine that your circle of benefactors begins to dissolve into white light, and gather that white light into your heart center. Rest your mind and relax."
    },
    meditationTypes: {
      title: "Meditation: Formal vs Informal",
      icon: "logo",
      formal: {
        title: "Formal Meditation",
        content: "Sitting everyday and paying attention to your breath. Build to 20 mins or more.",
        posture: [
          { point: "Seat", instruction: "Whether on cushion or chair, the seat should be flat — not tilting" },
          { point: "Legs", instruction: "Crossed comfortably in front, or feet flat on floor if in chair" },
          { point: "Torso", instruction: "Upright with strong back and open front. Don't lean back" },
          { point: "Hands", instruction: "Open, palms down, resting on thighs" },
          { point: "Eyes", instruction: "Open, indicating attitude of remaining awake and relaxed. Gaze slightly downward, 4-6 feet ahead" },
          { point: "Mouth", instruction: "Slightly open so jaw is relaxed. Tip of tongue on roof of mouth" }
        ],
        breathExercise: {
          title: "Breath Count Exercise",
          instruction: "Count each exhale. Once you get to 5, start over at 1. Continue for several cycles. Notice if your mind wanders and you end up at 20! Then practice non-judging."
        }
      },
      informal: {
        title: "Informal Meditation",
        types: [
          { name: "Guided Meditation", description: "Following a pre-recorded voice. YouTube and apps like Calm have excellent options." },
          { name: "Body Scan Meditation", description: "Scanning the body, clearing pain, painting with color or light, breathing into different areas." },
          { name: "A Thought is Just a Thought", description: "Observing thoughts — how they come, where they go. Observing rather than becoming the thought." },
          { name: "Dealing with Difficult Emotions", description: "Best guided with a coach to work through difficult situations." },
          { name: "Self Growth & Awareness", description: "May take many forms. The Loving Kindness Meditation has been widely used for many years." }
        ]
      }
    },
    quantumPsychology: {
      title: "Why Quantum Psychology?",
      icon: "logo",
      comparison: {
        other: {
          title: "Other Forms of Therapy",
          purpose: "Heal the self, heal unfinished business, reform or create a more solid ego state, position you to do more, have more, be more successful."
        },
        qp: {
          title: "Quantum Psychology",
          purpose: "Purpose is to find out who you are."
        }
      },
      oneSubstance: {
        title: "The One Substance",
        content: "According to Quantum Physics, everything is made up of matter. Consciousness forms many illusions — separateness. Think of a bathtub with water. Take out water and make ice cubes, put them back in. Now we have tub, water, and ice cubes. We perceive these as different, but they came from the same water and can return to that form."
      },
      keyInsight: "Know who you are NOT to know who you ARE."
    },
    nervousSystemCoding: {
      title: "How Our Nervous System Codes",
      icon: "logo",
      examples: [
        "We do good → we get praise, gifts, treats",
        "We do what parents ask → we get hugs, smiles, approval, love",
        "We do good in school → we get praise; do bad → we get scolded",
        "We're happy → we get attention; angry or sad → we're told we can't feel that way"
      ],
      keyPrinciple: {
        title: "Human Potential Movement",
        content: "ALL NEUROSIS IS CAUSED BY AN INTERRUPTION OF THE OUTWARD MOTION. E means outward (energy), Motion means movement. When a child's anger is thwarted, that energy must go somewhere. It turns back on self, becomes stuck — like a kink in a hose. Now instead of free-flowing energy, it becomes frozen with weight, mass, and time frozen."
      }
    },
    suggestion: {
      title: "The Suggestion",
      icon: "logo",
      content: "If we can take the story off of why we are 'Angry' and just sit with the experience of 'anger,' it might be interesting to see what happens. Take the label off and allow it to be what it is.",
      warning: "If we try to change things or transmute things or make it different in any way, you are RESISTING IT. There is an expression: 'What we RESIST PERSISTS.'",
      guidance: "You don't want to move it to another Chakra, give it to God, get rid of it any way. You want to allow it to be energy and explore it for what it is."
    },
    ninePrincipals: {
      title: "9 Principals of Quantum Psychology",
      icon: "logo",
      principals: [
        "There is only one substance",
        "What you know about yourself came from outside of yourself, therefore discard it",
        "Question everything, do not believe anything",
        "In order to find out who you are, you must find out who you are not",
        "In order to let go of something, you must first know what it is",
        "The experiencer is contained within the experience itself",
        "Anything you think you are... you are not",
        "Hold onto the I AM, let go of everything else",
        "Anything you know about... you cannot be"
      ]
    },
    books: [
      { title: "Mindfulness for Beginners", author: "Jon Kabat-Zinn" },
      { title: "Any title by Pema Chödrön", author: "" },
      { title: "The Practice of Pure Awareness", author: "Reginald Ray", note: "For the fairly serious meditator — incredible body meditations" }
    ],
    closingThought: "Exploring the 'No State State' of NO Thoughts, Memories, Emotions, Associations, Perceptions, Attentions, or Intentions — this is where we find who we truly are."
  },

  'creating-harmony': {
    id: 'creating-harmony',
    title: 'Creating Harmony',
    subtitle: 'Finding Balance in All Areas of Life',
    image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800',
    introduction: "Oh the human pursuit for balance! I would say in fact... I do not prefer the term balance, rather I do prefer the term harmony and I prefer the term 'creating' over 'finding'. Looking for anything implies it's hiding or lost, like a good game of hide and seek.",
    keyInsight: {
      title: "A Different Perspective",
      icon: "logo",
      content: "Balance is not hiding from you, to torture you, or elude you. From an early age we may receive the message that 'we are not ok, the way we are!' Many of us become overachievers, comfort or security seekers, and/or pleasers. Striking a balance is really no different than a form of overachieving, comfort or pleasing."
    },
    reflectionQuestion: "How many things are on your 'To Do' list that are for other people or to prove yourself and your worth?",
    circleOfPerspective: {
      title: "Circle of Personal Perspective",
      icon: "logo",
      intro: "At times it can be important to look at life from all perspectives and what is known as a Helicopter Perspective—the ability to look from above to gain a global view.",
      areas: [
        { name: "Health", icon: "logo" },
        { name: "Wealth", icon: "logo" },
        { name: "Family & Friends", icon: "logo" },
        { name: "Playtime, Hobbies & Fun", icon: "logo" },
        { name: "Relationships", icon: "logo" },
        { name: "Contribution / Spirituality", icon: "logo" },
        { name: "Personal Space", icon: "logo" },
        { name: "Career / Job", icon: "logo" }
      ],
      purpose: "This tool is designed to point in A direction, not necessarily THE direction. It can bring a lot of clarity and structure towards the direction we wish to head and the areas we can pay attention to."
    },
    coachingQuestions: {
      title: "Self-Discovery Questions",
      icon: "logo",
      questions: [
        "What goals would you like to achieve with this coaching experience?",
        "What does achieving this goal mean to you? How will you feel? What will it look like?",
        "What evidence will you have when you achieve this goal? What will you see, hear and/or feel?",
        "Where and with whom do you want to accomplish this goal?",
        "What actions are you willing to take to achieve this goal?",
        "When you achieve this goal, what will be the impact on other aspects of your life?",
        "Thinking back, what roadblocks got in the way of this goal?",
        "You already have great skills, tools and resources. Which will help you achieve this goal?",
        "What other resources do you need to accomplish this goal?",
        "What action steps will you take to acquire these new resources?"
      ]
    },
    rovingPriorities: {
      title: "Roving Priorities & Energy Alignment",
      icon: "logo",
      intro: "An all too common challenge and source of great frustration is the sense of making whichever state or activity we are currently living in the number one priority ALL the time. So when we are at work, that is number one. Then we go to the gym, and we judge our bodies. We meet with friends and suddenly our social life is the priority forgetting we have work in the AM.",
      insight: "This is literally impossible, unrealistic and may cause a great deal of stress as well as a sense of failure.",
      poohReference: {
        title: "The Bisy Backson",
        content: "If you recall Winnie the Pooh, you may recall the Bisy Backson—roughly translated to 'Busy... Back Soon.' Bisy was an exhaustively hyper character easily looped into doing everything and anything, feeling they were missing out if not up to something. We really could learn a great deal about ourselves through the characters of Winnie the Pooh."
      },
      categories: [
        { name: "Health", subcategories: ["Fitness", "Emotional health", "Stress reduction"] },
        { name: "Wealth/Financial", subcategories: ["Earnings", "Savings", "Spending"] },
        { name: "Social", subcategories: ["Family of creation", "Family of origin", "Friends"] },
        { name: "Playtime, Hobbies, Fun", subcategories: [] },
        { name: "Relationships", subcategories: ["Significant other", "Close friends", "Parents", "Children", "Boss"] },
        { name: "Personal Space", subcategories: [] },
        { name: "Contribution/Spirituality", subcategories: ["Volunteering"] }
      ],
      exercise: "Rank in order of priority these categories and subcategories. Create your own as required—this exercise reflects YOUR life and priorities. Discover how your energy is aligned with your priorities. I suggest using a pencil, because you will be erasing and rewriting many times."
    },
    closingThought: "What if we could shift this perception, look at it a little differently... what if we could Create Harmony with ourselves, with Life?"
  },

  'empath-enhancement-guide': {
    id: 'empath-enhancement-guide',
    title: 'The Empath Enhancement Guide',
    subtitle: 'Understanding & Thriving as a Sensitive Soul',
    image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800',
    introduction: "The formation and experience of being an empath can be viewed through both earthly (psychological and physiological) and sacred (spiritual and energetic) lenses. Here's a comprehensive exploration:",
    earthlyExplanation: {
      title: "An Earthly Explanation",
      icon: "logo",
      psychologicalRoots: {
        title: "Psychological Roots",
        items: [
          { name: "Early Childhood Experiences", description: "Empaths often come from environments where they had to be highly attuned to the emotions of others for survival, such as growing up in homes with emotional volatility, trauma, or neglect. As a result, they learn to be hyper-aware of emotional cues." },
          { name: "Highly Sensitive Person (HSP) Traits", description: "Being an empath is often linked to being a Highly Sensitive Person (HSP), a concept explored by psychologist Dr. Elaine Aron. HSPs have a more sensitive nervous system, making them more attuned to subtle stimuli, including emotional energy." },
          { name: "Neuroscience", description: "Mirror neurons in the brain are responsible for the ability to feel what others are feeling. Empaths might have more active or finely tuned mirror neuron systems, leading them to physically and emotionally experience others' feelings more deeply." },
          { name: "Intuition and Emotional Intelligence", description: "Empaths may have a natural proclivity toward high emotional intelligence, allowing them to deeply understand and sense the inner world of others." }
        ]
      },
      physiologicalAspects: {
        title: "Physiological Aspects",
        items: [
          { name: "Nervous System Sensitivity", description: "Empaths tend to have a more sensitive nervous system, leading them to be easily overstimulated by external factors, including loud environments, strong emotions, and chaotic surroundings." },
          { name: "Cortisol and Stress Responses", description: "Since empaths are constantly taking in emotional information, they may experience higher levels of stress hormones like cortisol. This can lead to burnout or emotional exhaustion if they don't establish proper boundaries." }
        ]
      }
    },
    sacredExplanation: {
      title: "Sacred/Spiritual Explanation",
      icon: "logo",
      energeticSensitivity: {
        title: "Energetic Sensitivity",
        intro: "From a sacred perspective, empaths are often seen as individuals with heightened energetic sensitivity. This could be due to:",
        items: [
          { name: "Spiritual Mission", description: "Some spiritual traditions view empaths as souls who have chosen to come to Earth with a specific purpose: to heal, guide, and help others by transmuting emotional energy and holding space for collective healing." },
          { name: "Past Lives or Karmic Path", description: "In many spiritual frameworks, empaths are believed to have developed their sensitivity over multiple lifetimes. They may have chosen to incarnate with heightened empathy as part of their soul's journey toward growth and enlightenment." },
          { name: "Connection to the Divine", description: "Empaths are sometimes considered to have an open connection to higher realms, allowing them to intuitively pick up on the energies of others. Their energy fields are more porous, making them more susceptible to feeling everything around them on a deep, energetic level." }
        ]
      },
      chakraConnections: {
        title: "Energy and Chakras",
        items: [
          { name: "Open Solar Plexus Chakra", description: "The solar plexus chakra, located in the abdomen, is considered the energy center for personal power and self-esteem. Empaths may have an open or unbalanced solar plexus, leading them to absorb external emotions without realizing it. Grounding and strengthening this chakra can help with creating emotional boundaries." },
          { name: "Clairsentience", description: "In spiritual terms, empaths often possess clairsentient abilities, meaning they can intuitively feel the emotions and energies of others on a spiritual level, even if the person is not physically present." }
        ]
      }
    },
    empathExperience: {
      title: "The Experience of Being an Empath",
      icon: "logo",
      experiences: [
        { name: "Emotional Overload", description: "Empaths feel other people's emotions intensely, sometimes confusing others' feelings with their own. This can lead to mood swings or sudden emotional shifts that don't have an obvious source." },
        { name: "Sensitivity to Environments", description: "Crowded places, noisy environments, and chaotic energy can overwhelm an empath. They may feel drained after being around others for extended periods, especially if there's negative or intense emotional energy." },
        { name: "Deep Compassion and Intuition", description: "Empaths tend to have a natural ability to deeply connect with others. They can often sense underlying emotions, even those that others are trying to hide. This makes them highly compassionate and supportive friends and healers." },
        { name: "Difficulty with Boundaries", description: "One of the biggest challenges for empaths is distinguishing their own emotions from those of others. Without clear boundaries, they can take on emotional burdens that aren't theirs, leading to fatigue, anxiety, or even physical symptoms like headaches." }
      ]
    },
    strengthsAndChallenges: {
      title: "Understanding The Empath",
      icon: "logo",
      strengths: {
        title: "The Strengths of Being an Empath",
        items: [
          "Deep understanding of others",
          "Natural healers and connectors",
          "Heightened creativity and intuition"
        ]
      },
      challenges: {
        title: "Challenges Empaths Face",
        items: [
          "Emotional overwhelm and overstimulation",
          "Difficulty distinguishing personal emotions from others",
          "Burnout and compassion fatigue"
        ]
      }
    },
    protectingEnergy: {
      title: "Protecting Energy and Creating Boundaries",
      icon: "logo",
      sections: [
        {
          title: "Grounding Techniques",
          items: [
            { name: "Earthing", description: "Spending time in nature, walking barefoot on the ground, and connecting to the Earth's energy can help discharge excess emotional energy." },
            { name: "Breathing Exercises", description: "Slow, deep breathing can calm the nervous system and help empaths recenter themselves." },
            { name: "Root Chakra Meditation", description: "Focusing on the root chakra, associated with stability and grounding, helps empaths feel more secure in their energy." }
          ]
        },
        {
          title: "Energy Protection Practices",
          items: [
            { name: "Shielding", description: "Visualizing a protective barrier, such as a bubble of light around the body, can help empaths block out unwanted energies. Some people envision mirrors reflecting others' energies back to them or use different colors of light for different purposes (e.g., white for purity, blue for peace)." },
            { name: "Crystals", description: "Certain crystals, like black tourmaline, amethyst, or selenite, are believed to protect against negative energies and help empaths stay grounded. Wearing or carrying them can serve as an energetic shield." },
            { name: "Salt Baths", description: "Soaking in a salt bath (using Epsom or sea salt) is a popular way for empaths to cleanse their energy fields after absorbing others' emotions." }
          ]
        },
        {
          title: "Emotional Boundaries",
          items: [
            { name: "Self-Awareness", description: "The first step to protecting energy is becoming aware of when you're picking up on someone else's emotions. Regular check-ins with yourself, asking, 'Is this mine, or am I feeling someone else's energy?' can help identify emotional absorption." },
            { name: "Saying No", description: "Learning to say no to activities or situations that feel overwhelming is essential. Empaths often feel guilty for turning people down, but practicing assertiveness is key to maintaining emotional health." },
            { name: "Detaching with Love", description: "For empaths, it's important to cultivate compassionate detachment. This means offering love and support without becoming emotionally entangled in someone else's experience. Visualizing cords being cut or dissolved after emotional interactions can help." }
          ]
        },
        {
          title: "Spiritual Practices",
          items: [
            { name: "Daily Meditation", description: "Meditation helps empaths tune into their own energy and establish a sense of peace and clarity, making it easier to distinguish between their emotions and external influences." },
            { name: "Journaling", description: "Writing down emotional experiences helps empaths process what they're feeling and separate personal emotions from those of others." },
            { name: "Calling on Higher Guides", description: "Some empaths work with spiritual guides or deities for protection, asking for assistance in shielding their energy from negativity." }
          ]
        }
      ]
    },
    toolsForThriving: {
      title: "Tools for Survival and Thriving",
      icon: "logo",
      tools: [
        {
          title: "Energy Awareness and Protection",
          items: [
            "Identifying when energy is 'not yours'",
            "Grounding techniques (visualization, walking barefoot, breathing exercises)",
            "Shielding methods like imagining an energy bubble or light around you"
          ]
        },
        {
          title: "Setting Boundaries",
          items: [
            "Recognizing the importance of saying no",
            "Practical scripts for turning down obligations",
            "Differentiating between helping and enabling"
          ]
        },
        {
          title: "Self-Care Practices",
          items: [
            "Daily rituals for emotional detox (journaling, baths with Epsom salts, mindfulness)",
            "Balancing alone time and social interaction",
            "Restoring energy through nature, music, or creative outlets"
          ]
        },
        {
          title: "Managing Relationships",
          items: [
            "How to communicate your needs effectively",
            "Avoiding relationships that drain or manipulate your energy",
            "Building a support network of like-minded individuals"
          ]
        }
      ]
    },
    practicalExercises: {
      title: "Practical Exercises",
      icon: "logo",
      exercises: [
        { name: "Energy Scanning and Clearing", description: "A guided meditation or visualization to identify and release heavy energy." },
        { name: "Boundary Setting Role-Play", description: "Practice saying no in different scenarios." },
        { name: "Empath's Toolkit Creation", description: "A checklist or toolkit for maintaining balance (e.g., favorite tools, affirmations, or go-to grounding methods)." }
      ]
    },
    books: [
      { title: "The Untethered Soul", author: "Michael Singer" },
      { title: "The Tao of Pooh", author: "Benjamin Hoff" },
      { title: "Quiet: The Power of an Introvert in a World That Can't Stop Talking", author: "Susan Cain" },
      { title: "The Highly Sensitive Person", author: "Elaine N. Aron" },
      { title: "The Empath's Survival Guide", author: "Judith Orloff" },
      { title: "The Power of Now", author: "Eckhart Tolle" },
      { title: "Sensitive Is the New Strong", author: "Sydney Campos" }
    ],
    futureAspects: {
      title: "Future Aspects for Consideration",
      icon: "logo",
      items: [
        "3D / 5D aspects",
        "Science vs Sacred",
        "Birth Order",
        "Understanding The Shadow and The Circle of Love",
        "Using Metaphor for healing",
        "Moving from Surviving to Thriving",
        "Practicing honouring our internal compass vs dismissing intuitive guidance"
      ]
    },
    closingThought: "By integrating both earthly and sacred perspectives, empaths can navigate their sensitivity in a way that nurtures both their personal well-being and their connection to others. Being an empath is a profound gift but requires proper care, including self-care and boundary setting, to avoid burnout."
  },
  'living-beyond-self-sabotage': {
    id: 'living-beyond-self-sabotage',
    title: 'Living Beyond Self-Sabotage',
    subtitle: 'Knowing Our Parts to Live in Harmony',
    image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800',
    downloadUrl: 'https://customer-assets.emergentagent.com/job_hello-world-4550/artifacts/okp3jtft_Self_Sabotage_Inner_Parts_Workshopnewest.pptx',
    introduction: "Self-sabotage isn't a character flaw—it's a protective pattern. When we understand the inner parts driving these behaviors, we can move from conflict to harmony. This workshop explores the landscape of the psyche and offers tools for compassionate self-leadership.",
    keyInsight: "Nothing inside you is random or broken.",
    agenda: [
      "Our Inner Parts",
      "Quieting the Inner Critic",
      "Dancing With the Shadow",
      "Compassionate Inner Communication",
      "The 'I Know Better But Do Not Do It' Loop",
      "The Mother Wound and Self-Care Resistance",
      "The Nervous System Beneath Self-Sabotage",
      "Archetypes and Self-Leadership"
    ],
    sections: [
      {
        title: "Our Inner Parts",
        icon: "logo",
        content: "The psyche is the entire inner landscape: mind, subconscious, unconscious, ego, shadow, soul, body, emotions, and deeper conscious mind. Nothing inside you is random or broken.",
        items: [
          { term: "Psyche (the whole)", definition: "Holds all your inner resources, wounds, stories, and potential. Expressed through thoughts, emotions, sensations, dreams, behaviors, creativity, intuition." },
          { term: "Conscious Mind", definition: "What you are aware of right now. Logic, intention, decision-making. Often confused as 'being in control'." },
          { term: "Subconscious", definition: "Habits, emotional memory, learned safety. Runs automatic patterns. Reflected in body responses: tension, posture shifts, gut feelings." },
          { term: "Unconscious", definition: "Deep symbolic and instinctual layer. Archetypes, collective themes, forgotten material. Speaks through dreams, symbols, art, synchronicities." },
          { term: "Ego", definition: "The identity manager—'Who I think I am.' Protects from overwhelm. Often resists change to preserve safety." },
          { term: "The Shadow", definition: "Disowned or misunderstood parts. Not bad—simply unseen. Often holds hidden strengths, creativity, power, sensuality, intuition." }
        ]
      },
      {
        title: "How They Show Up",
        icon: "logo",
        content: "Understanding how these parts manifest helps us recognize patterns and create change.",
        items: [
          { term: "Behavior", definition: "Repeating patterns, avoidance or procrastination, self-sabotage loops" },
          { term: "Body", definition: "Chronic tension or pain, fatigue or shutdown, somatic signals before thoughts" },
          { term: "Dreams", definition: "Symbolic messaging, emotional processing, unintegrated material surfacing" },
          { term: "Triggers", definition: "Over-reactions, strong judgments, emotional flooding or withdrawal" },
          { term: "Patterns", definition: "Same relationship dynamics, cycles of motivation and collapse, 'I know better but don't do it'" }
        ]
      },
      {
        title: "The Shadow (Dancing With the Hidden Self)",
        icon: "logo",
        content: "The shadow contains our disowned parts—not bad, simply unseen. It often holds our hidden strengths.",
        insight: "When you say 'that's not me'—often it's a clue it IS you, in shadow.",
        items: [
          { term: "Projections", definition: "Judging others harshly for traits you secretly carry" },
          { term: "Triggers", definition: "Being 'set off' by something small" },
          { term: "Sabotage", definition: "Repeating self-destructive behaviors" },
          { term: "Hidden gifts", definition: "Creativity, power, sensuality, intuition locked away" }
        ]
      },
      {
        title: "Quieting Inner Conflict",
        icon: "logo",
        content: "Resistance is protective. Pushing creates more pushback. Curiosity softens defenses.",
        insight: "Tone matters more than words. Shame dysregulates the nervous system. Safety allows change.",
        questions: [
          "Who does this sound like?",
          "Is this helpful?",
          "Does this voice have a point?"
        ]
      },
      {
        title: "The 'I Know Better But Do Not Do It' Loop",
        icon: "logo",
        content: "This familiar loop isn't a failure of willpower—it's a signal from the nervous system.",
        insight: "The loop disappears or dissipates its power once we see where we are on the loop.",
        items: [
          { term: "Relate to the self", definition: "Get curious about the process and why we are where we are" },
          { term: "Curiosity interrupts self-judgment", definition: "And can lead to compassion" },
          { term: "Ask: What part might be fearful?", definition: "Compassion and understanding may help move energy" }
        ]
      },
      {
        title: "The Mother Wound & Self-Care Resistance",
        icon: "logo",
        content: "The mother wound refers to the imprint left by early caregiving experiences—not just with our mother, but with how care, safety, and attunement were offered or withheld.",
        insight: "When this softens, self-care no longer feels selfish, and care begins to feel safe.",
        items: [
          { term: "Guilt around receiving", definition: "Feeling that needs must be earned" },
          { term: "Over-functioning patterns", definition: "Strong attachment to rescuing/mothering of others" },
          { term: "Interrupted nurturing", definition: "Patterns passed through generations, shaped by stress and survival" },
          { term: "Learning to re-mother the self", definition: "Offering ourselves steadiness, permission, and compassion" }
        ]
      },
      {
        title: "The Nervous System Beneath Sabotage",
        icon: "logo",
        content: "Understanding the nervous system helps us see why we do what we do—even when we 'know better.'",
        items: [
          { term: "Fight, Flight, Freeze, Façade, Fawn", definition: "Survival responses that drive automatic behavior" },
          { term: "Motivation depends on regulation", definition: "We can't push through dysregulation sustainably" },
          { term: "Consistency requires safety", definition: "The nervous system must feel safe to allow lasting change" }
        ]
      },
      {
        title: "Archetypes & Self-Leadership",
        icon: "logo",
        content: "We all have inner archetypes—parts that play different roles. Each has a positive intent.",
        insight: "Leadership comes from integration, not elimination.",
        items: [
          { term: "Protector", definition: "Keeps us safe, sometimes at the cost of growth" },
          { term: "Caregiver", definition: "Nurtures others, sometimes at the cost of self" },
          { term: "Saboteur", definition: "Actually trying to protect—from failure, rejection, or the unknown" }
        ]
      }
    ],
    reflection: {
      title: "Reflection Questions",
      icon: "logo",
      questions: [
        "Where do you notice resistance in yourself?",
        "What part might be trying to protect you?",
        "What would safety look like instead of force?"
      ]
    },
    closingThought: "Self-sabotage is not the enemy. It's a messenger. When we learn to listen with curiosity instead of judgment, we discover that every part of us is trying to help—even the ones that seem to be working against us. Integration, not elimination, is the path to harmony."
  },
  'forward-momentum': {
    id: 'forward-momentum',
    title: 'Forward Momentum',
    subtitle: "What's Driving You (Whether You Know It or Not)",
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/inciqrkk_theroadahead-160115123008-thumbnail-4.webp',
    introduction: "Sometimes a piece of information doesn't just teach you something… It rearranges how you see your entire life.\n\nMany years ago I watched a video from Tony Robbins... and honestly it brought me a lot of... food for thought let's say. I give credit to Tony Robbins for the below... it didn't come from me... it has been digested by me, however.",
    personalStory: {
      title: "My Journey to This Understanding",
      icon: "logo",
      paragraphs: [
        "I felt aimless. Just DOING. Just moving through life as you do. One thing leads to the next. I didn't have a lot of forward focus. I didn't know how to forward focus. My young life was spent mostly in chaos which shaped me strongly... maybe most significantly in the area of ADHD. Which is really a symptom of overwhelm. More on that in a different space.",
        "So moving through life coaching courses, other coaching, personal training... getting as good as I could at what I was doing. But something was missing for sure. Scrambling around life like a busy bee.",
        "But then the Rapture came for me... in the form of cancer crisis. Always or usually a wake up call for most and to consider doing it differently or finding a new way... I definitely didn't know what the way was.",
        "I do seem to be wired a little different. Maybe called Neurodivergent... maybe I'm just unique like you and everyone else. I have a unique fingerprint and therefore I am UNIQUELY ME, you are UNIQUELY YOU. It's been very important for me to discover this wiring and meet myself there. I tend to be defiant, sceptical but also 'obedient', people pleaser-y. Sooooo, really... I have a fair bit of inner conflict. Useful to say 'Part of Me' when getting to the business of knowing self.",
        "Being more 'defiant' than compliant, often expresses in the 'real world' roughly like this; I set a goal... and can pretty much guarantee I won't make it there. Some call it self sabotage... but is it?? Or is it simply the way I approach myself?",
        "What that looked like for me... acting like everyone else, the achieving people, the goal setters and the goal getters — like people set goals and got to them!!! I could NOT relate! Drove me mad! AND... made me a little dead inside. All the COMMON GUIDANCE... left me feeling lost, alone, feeling even more like a failure. SMART principles... forget about it... the worst for me!! Defy defy defy! But didn't know why.",
        "I finally found Holistic Coaching, which is digging deeper into our conditioning, subconscious, how we shaped ourselves about our early life experiences. Now this was REALLY a wake up for me. Several life coaching courses later and really I wasn't any further ahead... maybe feeling even more like a failure.",
        "But Holistic Coaching... turned out was my JAM! It was the guidance for life, self, this sacred journey, looking beyond the literal world. Cuz I had questions!! That I WAS NOT getting guidance with. Like why could I not reach a goal? Likely 40% of the population will even hear this. But it will really hit 20%. I felt like such a failure... I just stopped trying."
      ],
      insight: "What this looks like — laziness. Laziness is rarely laziness... it's typically a sense of low self worth and expectation of failure... so why even bother?!?!?!",
      closing: "This subconscious work was so important for me! And maybe most importantly... discovering who and how I truly was/am. Gaining compassion for that and meeting myself where I was."
    },
    beforeMomentum: {
      title: "Before Momentum… There's Pattern",
      icon: "logo",
      intro: "We like to think we're making conscious choices. But most of us are running a pattern. A system. A kind of internal operating code built from:",
      patterns: [
        "Early experiences",
        "Emotional moments",
        "Decisions we made in seconds… that lasted decades"
      ],
      story: "Tony shares a story from when he was 11. No food at Thanksgiving because his father was unemployed. Stress in the home, someone dropped off dinner…",
      meanings: [
        { person: "His father", meaning: "This is charity. I'm worthless.", result: "And he left." },
        { person: "Tony", meaning: "Strangers care.", result: "That one meaning turned into a lifetime of contribution… feeding millions of people." }
      ],
      insight: "Same event. Different meaning → different identity → different life."
    },
    questionNeverAsked: {
      title: "The Question Most People Never Ask",
      icon: "logo",
      notThese: [
        "What do I want?",
        "Why am I stuck?"
      ],
      butThese: [
        "What shaped me?",
        "What did I decide back then… that I'm still living out now?"
      ],
      insight: "Because those decisions — they become your default map."
    },
    leadSystem: {
      title: "Your 'Lead System': The Invisible Steering Wheel",
      icon: "logo",
      intro: "We all share the same six core needs. But here's the twist: You don't prioritize them equally. One or two tend to run the show. This is your lead system.",
      determines: [
        "Your decisions",
        "Your habits",
        "Your relationships",
        "Your risks",
        "Your version of 'success'"
      ],
      example: {
        title: "Same Needs, Different Lives",
        description: "Two people both crave significance.",
        paths: [
          { label: "One becomes", items: ["A firefighter", "A leader", "Someone who protects and serves"] },
          { label: "Another", items: ["Seeks control through fear", "Uses violence", "Or lives in chronic conflict"] }
        ],
        insight: "Same need. Different map."
      }
    },
    certaintyUncertainty: {
      title: "Certainty & Uncertainty: The Built-In Paradox",
      icon: "logo",
      certainty: {
        intro: "We all need certainty — safety, comfort, knowing we'll be okay. Without it, nothing else matters.",
        thought: "If your world feels unstable… you're not thinking about growth, purpose, or contribution. You're thinking: 'Am I okay?'"
      },
      uncertainty: {
        intro: "But here's the twist: If you had total certainty… you'd be bored out of your mind. Predictable everything = emotional flatline.",
        needs: ["Surprise", "Change", "The unknown"],
        insight: "We love the surprises we want… and call the ones we don't 'problems.' But both are doing the same thing: They are injecting life back into the system."
      }
    },
    meetingNeeds: {
      title: "How We Meet Our Needs (Consciously or Not)",
      icon: "logo",
      intro: "This part is subtle… but massive. We don't just have needs. We have strategies for meeting them. And not all strategies are… ideal.",
      examples: [
        { need: "Certainty", healthy: ["Building a skill", "Creating stability"], unhealthy: ["Controlling everything", "Giving up entirely"] },
        { need: "Significance", healthy: ["Growth and mastery"], unhealthy: ["Comparison and conflict", "Even destruction"] }
      ],
      insight: "The need itself is not the problem. The strategy is everything."
    },
    fourVsTwo: {
      title: "The First Four vs The Last Two",
      icon: "logo",
      personalityNeeds: {
        title: "The First Four (Personality Needs)",
        needs: ["Certainty", "Uncertainty", "Significance", "Love/Connection"],
        quote: "You will meet these no matter what. Even if you have to lie to yourself to do it."
      },
      spiritualNeeds: {
        title: "The Last Two (Spiritual Needs)",
        needs: ["Growth", "Contribution"],
        description: "This is where fulfillment lives. Not happiness. Not comfort. Fulfillment.",
        warning: "Without these? You can have money, relationships, success… and still feel like something is quietly missing."
      }
    },
    growthFeelsHard: {
      title: "The Real Reason Growth Feels Hard",
      icon: "logo",
      content: "Growth isn't just effort. It's identity disruption. Because if you grow… you outgrow the version of you that felt familiar. And the nervous system doesn't always celebrate that. It resists. Not because growth is wrong… but because unfamiliar feels unsafe."
    },
    meaningMovement: {
      title: "The Secret Thread: Meaning → Movement",
      icon: "logo",
      content: "Every experience you've had… You gave it meaning. That meaning shaped your beliefs, your identity, your map. And now? That map is either creating momentum… or quietly looping you in place."
    },
    forwardMomentum: {
      title: "Forward Momentum Isn't About More Effort",
      icon: "logo",
      intro: "It's about alignment between:",
      alignments: [
        { name: "Your Target", question: "Do you actually know where you're going?" },
        { name: "Your Map", question: "Is your strategy moving you forward… or keeping you safe?" },
        { name: "Your Lead System", question: "Which needs are driving you the most right now?" }
      ],
      checks: [
        "Are you chasing certainty?",
        "Craving significance?",
        "Avoiding uncertainty?",
        "Starving for connection?"
      ],
      truth: "You are always moving. The question is… Is it forward… or just familiar?"
    },
    quietReframe: {
      title: "A Quiet Reframe to Sit With",
      icon: "logo",
      intro: "If something in your life isn't moving, instead of asking 'Why can't I change?' Try:",
      questions: [
        "What need am I trying to meet right now?",
        "What strategy am I using to meet it?",
        "Where did I learn this strategy?",
        "And… is it still serving me?"
      ]
    },
    closingThought: "Forward momentum doesn't come from forcing yourself into a new life. It comes from understanding the system that created your current one… and gently, consciously, choosing a new direction. Not by becoming someone else. But by becoming more aware of who's been driving all along."
  },
  'intro-to-metaphysics': {
    id: 'intro-to-metaphysics',
    title: 'Introduction To Metaphysics',
    subtitle: 'When the Body Speaks, Listen',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/xwq5781e_original-9349871-1.jpg',
    introduction: "Most of us prefer to think of the body as just a body — a collection of bones, muscles, and organs that sometimes cooperate and sometimes don't. When pain shows up, we usually look for the nearest physical explanation: too much stress, a strained muscle, a hormone out of balance. Rarely do we stop to ask: what else might this be telling me?",
    bodyNeverLies: {
      title: "The Body Never Lies",
      icon: "logo",
      paragraphs: [
        "The truth is, our bodies are not only physical — they are plasma-and-particle vessels, alive with energy, constantly transmitting signals from deeper layers of who we are. If the mind is quick to spin stories, hide truths, or distract us with endless chatter, the body is refreshingly direct. It never lies.",
        "Much of what the body reveals is rooted in the subconscious — or what some would call the unconscious — the unseen layers where emotions, memories, and stress patterns quietly live. We may not always be aware of what we've tucked away, but the body is. It faithfully translates those hidden experiences into tangible form: tight shoulders, a heavy chest, a restless gut, or even long-term illness."
      ],
      insight: "And when you start to look for it, you realize… we already know this. We've just never been taught to trust it."
    },
    bodySignals: {
      title: "The Body Speaking in Real Time",
      icon: "logo",
      signals: [
        { signal: "Your stomach drops", meaning: "before bad news lands" },
        { signal: "Your chest tightens", meaning: "when you're anxious or overwhelmed" },
        { signal: "Your shoulders creep up", meaning: "when you're carrying too much" },
        { signal: "Your jaw clenches", meaning: "when something feels unfair or unspoken" },
        { signal: "Your throat tightens", meaning: "when you're holding back words" },
        { signal: "You feel butterflies", meaning: "when you're excited or stepping into something new" },
        { signal: "You feel physically heavy", meaning: "during grief" },
        { signal: "You feel energized and lighter", meaning: "when something feels aligned" }
      ],
      insight: "These aren't random reactions — they are the body speaking in real time."
    },
    symptomsAsMessages: {
      title: "Symptoms as Messages",
      icon: "logo",
      content: "From a metaphysical perspective, symptoms are not random flaws or punishments. They are messages — gentle (and sometimes not-so-gentle) invitations to listen more deeply.",
      questions: [
        "What is unspoken?",
        "What is unprocessed?",
        "What part of me needs to be heard or released?"
      ],
      note: "This approach does not replace medical care. Instead, it adds another layer of wisdom. It invites us to see the body not as something that betrays us, but as something that communicates with us — consistently, honestly, and often quite brilliantly.",
      insight: "So when the body speaks — through sensation, tension, or illness — perhaps the most powerful thing we can do… is listen."
    },
    bodyMap: {
      title: "Common Body Signals & What They May Reflect",
      icon: "logo",
      intro: "As you begin to tune into your body, you may notice patterns in where you feel things. These are not rules, but gentle reference points — places to get curious rather than certain.",
      areas: [
        { area: "Head", meaning: "Mental overload, overthinking, pressure, trying to control outcomes" },
        { area: "Jaw", meaning: "Suppressed frustration, unspoken words, holding tension" },
        { area: "Neck", meaning: "Resistance, difficulty seeing another perspective, stubbornness" },
        { area: "Shoulders", meaning: "Carrying responsibility, burdens, 'holding it all together'" },
        { area: "Chest / Heart Space", meaning: "Grief, love, emotional openness or protection" },
        { area: "Upper Back", meaning: "Feeling unsupported, unseen, or emotionally alone" },
        { area: "Lower Back", meaning: "Security concerns (money, safety, stability)" },
        { area: "Stomach / Gut", meaning: "Processing emotions, anxiety, 'gut instincts,' worry" },
        { area: "Solar Plexus", meaning: "Personal power, control, self-worth" },
        { area: "Hips", meaning: "Stored emotions, fear of moving forward, old emotional residue" },
        { area: "Pelvis / Reproductive", meaning: "Creativity, relationships, intimacy, identity" },
        { area: "Hands", meaning: "Control, giving/receiving, holding on vs letting go" },
        { area: "Legs", meaning: "Forward movement, direction in life, independence" },
        { area: "Knees", meaning: "Flexibility, ego, ability to bend and adapt" },
        { area: "Feet", meaning: "Grounding, stability, trust in your path" }
      ]
    },
    closingThought: "You don't need to analyze every sensation. Sometimes simply noticing — 'oh, my shoulders are tight today' — is enough to begin shifting the pattern. Awareness softens what force cannot."
  }
};
